import { useState } from "react";
import { Dialog, DialogHeader } from "@astryxdesign/core/Dialog";
import { Layout, LayoutContent, LayoutFooter } from "@astryxdesign/core/Layout";
import { HStack } from "@astryxdesign/core/HStack";
import { VStack } from "@astryxdesign/core/VStack";
import { Button } from "@astryxdesign/core/Button";
import { Text } from "@astryxdesign/core/Text";
import { Banner } from "@astryxdesign/core/Banner";
import type { QuotaExhausted } from "../../lib/api";

interface StreamContentionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** The camera the operator was trying to start. */
  cameraName: string;
  contention: QuotaExhausted;
  /** Absent for a viewer without cameras.manage, who is offered the request
   *  route below instead of stopping the stream themselves. */
  onConfirm?: () => void;
  /** Present for a viewer who cannot evict: asks somebody who can.
   *  Asking is not the privileged act — approving is. Returned as a promise
   *  (not fire-and-forget) so this dialog can show a pending state and, if
   *  it rejects, put the failure in front of the one person who can act on
   *  it — this is a viewer's only escalation path past a full wall, and
   *  failing silently here used to leave the dialog cheerfully offering the
   *  same button that had just failed. */
  onRequest?: () => Promise<void>;
  isConfirming: boolean;
  /** True once a request has been sent and is waiting for an answer. */
  isRequested?: boolean;
}

/**
 * Asks whether to stop a running stream to make room for another.
 *
 * This dialog is the reason eviction is acceptable at all. The original slot
 * budget refused when full and never evicted, on the grounds that silently
 * taking somebody's view away is a worse surprise than being told to wait —
 * and that was right about SILENT eviction. What makes it safe is that the
 * person causing it sees exactly what they are about to stop and how many
 * people are watching it, and everybody affected is told afterwards.
 */
export function StreamContentionDialog({
  isOpen,
  onOpenChange,
  cameraName,
  contention,
  onConfirm,
  onRequest,
  isConfirming,
  isRequested = false,
}: StreamContentionDialogProps) {
  const { scope, limit, candidate } = contention;
  const scopeLabel = scope === "project" ? "this site's limit" : "the system-wide limit";
  // Cleared at the START of every attempt (below), not relied on to reset by
  // remounting: the dialog stays mounted across a new contention appearing
  // without an intervening dismiss, so a stale error from a previous ask
  // could otherwise survive into a fresh one.
  const [requestError, setRequestError] = useState<string | null>(null);

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} purpose={onConfirm ? "required" : "info"} width={460}>
      <Layout
        header={
          <DialogHeader
            title="No free stream slots"
            subtitle={`${cameraName} can't start: ${scopeLabel} of ${limit} is already reached.`}
            onOpenChange={onOpenChange}
          />
        }
        content={
          <LayoutContent>
            <VStack gap={3}>
              {!candidate && (
                <Banner
                  status="info"
                  title="Nothing here can be stopped to make room"
                  description={
                    scope === "global"
                      ? "The streams using the remaining capacity are at sites you don't have access to. Ask a supervisor to free one, or raise the system-wide limit."
                      : "Try again shortly, or raise this site's limit."
                  }
                />
              )}

              {candidate && !onConfirm && (
                <VStack gap={2}>
                  <Banner
                    status={isRequested ? "success" : "info"}
                    title={
                      isRequested
                        ? "Request sent"
                        : "You can't stop another operator's stream yourself"
                    }
                    description={
                      isRequested
                        ? `Waiting for someone who manages cameras to approve stopping ${candidate.name}. You'll be told either way, and this tile starts on its own if it's approved.`
                        : `You can ask for ${candidate.name} to be stopped. Somebody who manages cameras decides, and everyone watching it is told.`
                    }
                  />
                  {!isRequested && (
                    <Text type="supporting" color="secondary">
                      {candidate.viewers === 0
                        ? "Nobody is watching it, so this is likely to be approved."
                        : `${candidate.viewers} ${candidate.viewers === 1 ? "person is" : "people are"} watching it.`}
                    </Text>
                  )}
                </VStack>
              )}

              {candidate && onConfirm && (
                <VStack gap={2}>
                  <Text>
                    Starting {cameraName} will stop <Text as="span" weight="semibold">{candidate.name}</Text>.
                  </Text>
                  {/* The viewer count is the whole point of showing this rather
                      than just doing it: stopping a stream nobody is watching
                      is free, and stopping one four people are watching is a
                      decision somebody should make knowingly. */}
                  <Text type="supporting" color="secondary">
                    {candidate.viewers === 0
                      ? "Nobody is watching it."
                      : `${candidate.viewers} ${candidate.viewers === 1 ? "person is" : "people are"} watching it right now.`}
                  </Text>
                  <Text type="supporting" color="secondary">
                    Everyone watching will be told what happened and who did it.
                  </Text>
                </VStack>
              )}

              {/* Kept here, beside the button that produced it, rather than a
                  toast — this dialog is the only escalation this viewer has
                  past a full wall, and on a wall a toast is gone in seconds.
                  Failing silently here (an unhandled rejection, no message,
                  isRequested left false) used to look like nothing had
                  happened at all. */}
              {requestError && (
                <Banner status="error" title="Couldn't ask for a slot" description={requestError} />
              )}
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter>
            <HStack gap={2} justify="end">
              <Button label={onConfirm && candidate ? "Cancel" : "Close"} variant="ghost" onClick={() => onOpenChange(false)} />
              {candidate && onConfirm && (
                <Button
                  label={`Stop ${candidate.name} and start`}
                  variant="destructive"
                  onClick={onConfirm}
                  isLoading={isConfirming}
                />
              )}
              {candidate && !onConfirm && onRequest && !isRequested && (
                <Button
                  label="Ask for a slot"
                  variant="primary"
                  // clickAction, not onClick + isLoading={isConfirming}:
                  // isConfirming is the PLAYER's "starting" state, which
                  // asking for a slot never enters — so this button never
                  // actually showed a pending state before. clickAction owns
                  // the pending state of the action it runs, and lets this
                  // dialog await the result to catch a failure.
                  clickAction={async () => {
                    setRequestError(null);
                    try {
                      await onRequest();
                    } catch (err) {
                      setRequestError(err instanceof Error ? err.message : "Something went wrong.");
                    }
                  }}
                />
              )}
            </HStack>
          </LayoutFooter>
        }
      />
    </Dialog>
  );
}
